import { Box, Card, CardContent, CircularProgress, Skeleton, Typography } from '@mui/material';
import { type ReactNode } from 'react';

interface JotCoachMetricCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  format?: 'number' | 'percentage';
  colorThresholds?: { green: number; yellow: number };
  loading?: boolean;
  icon?: ReactNode;
}

export function JotCoachMetricCard({
  title,
  value,
  subtitle,
  format = 'number',
  colorThresholds,
  loading = false,
  icon,
}: JotCoachMetricCardProps) {
  // Determine color based on thresholds
  const getValueColor = () => {
    if (!colorThresholds || typeof value !== 'number') {
      return 'text.primary';
    }

    if (value >= colorThresholds.green) {
      return 'success.main';
    }
    if (value >= colorThresholds.yellow) {
      return 'warning.main';
    }
    return 'error.main';
  };

  const formattedValue = () => {
    if (loading) return '';

    if (format === 'percentage') {
      return `${typeof value === 'number' ? value.toFixed(1) : value}%`;
    }
    return typeof value === 'number' ? value.toLocaleString() : value;
  };

  return (
    <Card elevation={2} sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Typography variant="subtitle2" color="text.secondary" fontWeight="medium">
            {title}
          </Typography>
          {icon && <Box color="primary.main">{icon}</Box>}
        </Box>

        {loading ? (
          <Box display="flex" alignItems="center" gap={2}>
            <CircularProgress size={24} />
            <Skeleton variant="text" width={100} height={40} />
          </Box>
        ) : (
          <>
            <Typography
              variant="h3"
              fontWeight="bold"
              color={getValueColor()}
              sx={{ mb: 1 }}
            >
              {formattedValue()}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
